import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, TextField, IconButton, Chip, CircularProgress, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl,
  InputLabel, Alert, Snackbar, Checkbox, ListItemText, TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Delete as DeleteIcon,
  Edit as EditIcon, CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import {
  fetchHolidays, createHoliday, updateHoliday, deleteHoliday,
} from '../../store/slices/leaveSlice';
import { AppDispatch, RootState } from '../../store/store';
import { DropdownItem, HolidayDto, CreateHoliday, UpdateHoliday } from '../../types';
import { administrationApi } from '../../api/administrationApi';

const isPastDate = (date: string) => {
    const today = new Date();
    const d = new Date(date);

    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);

    return d < today;
  };

const getDateOnly = (value: string) => value.split('T')[0];

type HolidaySortKey = 'srNo' | 'name' | 'date' | 'day' | 'description' | 'organization' | 'branch' | 'department' | 'status';
type SortDirection = 'asc' | 'desc';

const Holidays: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { holidays, loading } = useSelector((state: RootState) => state.leave);
  const user = useSelector((state: RootState) => state.auth.user);
  const tenantId = user?.tenantId ?? '';
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<HolidayDto | null>(null);
  const [organizations, setOrganizations] = useState<DropdownItem[]>([]);
  const [branches, setBranches] = useState<Array<DropdownItem & { organizationId?: string | null }>>([]);
  const [departmentItems, setDepartmentItems] = useState<Array<DropdownItem & { branchId?: string | null }>>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Form state
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<string[]>([]);
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [sortBy, setSortBy] = useState<HolidaySortKey>('srNo');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const hasDuplicateHolidayDate = (selectedDate: string) =>
    holidays.some((holiday) => {
      const isSameRecord = editing?.id === holiday.id;
      return !isSameRecord && getDateOnly(holiday.date) === selectedDate;
    });

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  useEffect(() => {
    dispatch(fetchHolidays({ year: yearFilter, searchTerm: normalizedSearchTerm || undefined }));
  }, [dispatch, yearFilter, normalizedSearchTerm]);

  useEffect(() => {
    if (!tenantId) return;

    Promise.all([
      administrationApi.getOrganizations(tenantId),
      administrationApi.getBranches(tenantId),
      administrationApi.getDepartments(tenantId),
    ])
      .then(([organizationList, branchList, departmentList]) => {
        setOrganizations(organizationList.map((item) => ({ id: item.id, name: item.organizationFullName })));
        setBranches(branchList.map((item) => ({ id: item.id, name: item.branchName, organizationId: item.organizationId })));
        setDepartmentItems(departmentList.map((item) => ({ id: item.id, name: item.departmentName, branchId: item.branchId })));
      })
      .catch(() => {
        setOrganizations([]);
        setBranches([]);
        setDepartmentItems([]);
        setSnackbar({ open: true, message: 'Unable to load organization hierarchy', severity: 'error' });
      });
  }, [tenantId]);

  const selectedOrganizationIdSet = new Set(selectedOrganizationIds);
  const selectedBranchIdSet = new Set(selectedBranchIds);
  const availableBranches = selectedOrganizationIds.length
    ? branches.filter((branch) => branch.organizationId && selectedOrganizationIdSet.has(String(branch.organizationId)))
    : branches;
  const availableBranchIdSet = new Set(availableBranches.map((branch) => String(branch.id)));
  const availableDepartments = selectedBranchIds.length
    ? departmentItems.filter((department) => department.branchId && selectedBranchIdSet.has(String(department.branchId)))
    : selectedOrganizationIds.length
      ? departmentItems.filter((department) => department.branchId && availableBranchIdSet.has(String(department.branchId)))
      : departmentItems;

  const branchById = new Map(branches.map((branch) => [String(branch.id), branch]));
  const departmentById = new Map(departmentItems.map((department) => [String(department.id), department]));

  const buildHolidayTargets = () => {
    const targetMap = new Map<string, { scopeType: 'Organization' | 'Branch' | 'Department'; scopeId: string }>();
    const selectedDepartmentBranchIds = new Set(
      selectedDepartmentIds
        .map((id) => departmentById.get(id)?.branchId)
        .filter((id): id is string => Boolean(id))
        .map(String)
    );

    selectedDepartmentIds.forEach((scopeId) => {
      targetMap.set(`Department:${scopeId}`, { scopeType: 'Department', scopeId });
    });

    selectedBranchIds.forEach((scopeId) => {
      if (!selectedDepartmentBranchIds.has(scopeId)) {
        targetMap.set(`Branch:${scopeId}`, { scopeType: 'Branch', scopeId });
      }
    });

    selectedOrganizationIds.forEach((scopeId) => {
      const hasSpecificBranch = selectedBranchIds.some((branchId) => String(branchById.get(branchId)?.organizationId) === scopeId);
      const hasSpecificDepartment = selectedDepartmentIds.some((departmentId) => {
        const branchId = departmentById.get(departmentId)?.branchId;
        return branchId && String(branchById.get(String(branchId))?.organizationId) === scopeId;
      });

      if (!hasSpecificBranch && !hasSpecificDepartment) {
        targetMap.set(`Organization:${scopeId}`, { scopeType: 'Organization', scopeId });
      }
    });

    return Array.from(targetMap.values());
  };

  const getApplicableForValue = () => {
    const selectedTypes = [
      selectedOrganizationIds.length ? 'Organization' : '',
      selectedBranchIds.length ? 'Branch' : '',
      selectedDepartmentIds.length ? 'Department' : '',
    ].filter(Boolean);

    return selectedTypes.length === 1 ? selectedTypes[0] : 'Mixed';
  };

  const getNames = (ids: string[], items: DropdownItem[]) =>
    ids.map((id) => items.find((item) => String(item.id) === id)?.name).filter(Boolean).join(', ') || '-';

  const getHolidayScopes = (holiday: HolidayDto) => {
    const targets = holiday.targets || [];
    const organizationIds = new Set<string>();
    const branchIds = new Set<string>();
    const departmentIds = new Set<string>();

    targets.forEach((target) => {
      const scopeId = String(target.scopeId);

      if (target.scopeType === 'Organization') {
        organizationIds.add(scopeId);
        return;
      }

      if (target.scopeType === 'Branch') {
        branchIds.add(scopeId);
        const branch = branchById.get(scopeId);
        if (branch?.organizationId) organizationIds.add(String(branch.organizationId));
        return;
      }

      if (target.scopeType === 'Department') {
        departmentIds.add(scopeId);
        const department = departmentById.get(scopeId);
        if (department?.branchId) {
          branchIds.add(String(department.branchId));
          const branch = branchById.get(String(department.branchId));
          if (branch?.organizationId) organizationIds.add(String(branch.organizationId));
        }
      }
    });

    if (!targets.length && holiday.applicableFor === 'Organization') {
      organizations.forEach((organization) => organizationIds.add(String(organization.id)));
    }

    if (!targets.length && holiday.applicableFor === 'Department' && holiday.departmentId) {
      departmentIds.add(holiday.departmentId);
      const department = departmentById.get(holiday.departmentId);
      if (department?.branchId) {
        branchIds.add(String(department.branchId));
        const branch = branchById.get(String(department.branchId));
        if (branch?.organizationId) organizationIds.add(String(branch.organizationId));
      }
    }

    return {
      organization: getNames(Array.from(organizationIds), organizations),
      branch: getNames(Array.from(branchIds), branches),
      department: getNames(Array.from(departmentIds), departmentItems),
    };
  };

  const handleSearch = () => {
    setSearchTerm((currentValue) => currentValue.trimStart());
  };

  const handleOpenAdd = () => {
    setEditing(null);
    setName('');
    setDate('');
    setDescription('');
    setSelectedOrganizationIds([]);
    setSelectedBranchIds([]);
    setSelectedDepartmentIds([]);
    setIsActive(true);
    setDialogOpen(true);
  };

  const handleOpenEdit = (h: HolidayDto) => {
    const organizationIds = new Set<string>();
    const branchIds = new Set<string>();
    const departmentIds = new Set<string>();

    (h.targets || []).forEach((target) => {
      const scopeId = String(target.scopeId);

      if (target.scopeType === 'Organization') {
        organizationIds.add(scopeId);
        return;
      }

      if (target.scopeType === 'Branch') {
        branchIds.add(scopeId);
        const branch = branchById.get(scopeId);
        if (branch?.organizationId) organizationIds.add(String(branch.organizationId));
        return;
      }

      if (target.scopeType === 'Department') {
        departmentIds.add(scopeId);
        const department = departmentById.get(scopeId);
        if (department?.branchId) {
          branchIds.add(String(department.branchId));
          const branch = branchById.get(String(department.branchId));
          if (branch?.organizationId) organizationIds.add(String(branch.organizationId));
        }
      }
    });

    setEditing(h);
    setName(h.name);
    setDate(h.date ? h.date.split('T')[0] : '');
    setDescription(h.description || '');
    setSelectedOrganizationIds(Array.from(organizationIds));
    setSelectedBranchIds(Array.from(branchIds));
    setSelectedDepartmentIds(Array.from(departmentIds));
    setIsActive(h.isActive);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name || !date) {
      setSnackbar({ open: true, message: 'Name and date are required', severity: 'error' });
      return;
    }

    if (hasDuplicateHolidayDate(date)) {
      setSnackbar({ open: true, message: 'A holiday already exists on this date', severity: 'error' });
      return;
    }

    const targets = buildHolidayTargets();

    if (targets.length === 0) {
      setSnackbar({ open: true, message: 'Select at least one organization, branch, or department', severity: 'error' });
      return;
    }

    try {
      const selectedApplicableFor = getApplicableForValue();
      if (editing) {
        const data: UpdateHoliday = {
          id: editing.id, name, date, description, applicableFor: selectedApplicableFor, targets,
          year: new Date(date).getFullYear(), isActive,
        };
        await dispatch(updateHoliday({ id: editing.id, data })).unwrap();
        setSnackbar({ open: true, message: 'Holiday updated', severity: 'success' });
      } else {
        const data: CreateHoliday = {
          name, date, description, applicableFor: selectedApplicableFor, targets,
          year: new Date(date).getFullYear(),
        };
        await dispatch(createHoliday(data)).unwrap();
        setSnackbar({ open: true, message: 'Holiday created', severity: 'success' });
      }
      setDialogOpen(false);
      dispatch(fetchHolidays({ year: yearFilter, searchTerm: normalizedSearchTerm || undefined }));
    } catch (err: any) {
      setSnackbar({ open: true, message: err || 'Operation failed', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await dispatch(deleteHoliday(id)).unwrap();
      setSnackbar({ open: true, message: 'Holiday deleted', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err || 'Delete failed', severity: 'error' });
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);
  const holidayRows = holidays.map((holiday, index) => ({
    holiday,
    originalIndex: index,
    scopes: getHolidayScopes(holiday),
  }));
  const sortedHolidayRows = [...holidayRows].sort((a, b) => {
    const getValue = (row: typeof holidayRows[number]) => {
      switch (sortBy) {
        case 'srNo':
          return row.originalIndex + 1;
        case 'name':
          return row.holiday.name;
        case 'date':
          return new Date(row.holiday.date).getTime();
        case 'day':
          return new Date(row.holiday.date).toLocaleDateString('en-US', { weekday: 'long' });
        case 'description':
          return row.holiday.description || '-';
        case 'organization':
          return row.scopes.organization;
        case 'branch':
          return row.scopes.branch;
        case 'department':
          return row.scopes.department;
        case 'status':
          return row.holiday.isActive ? 'Active' : 'Inactive';
        default:
          return '';
      }
    };

    const aValue = getValue(a);
    const bValue = getValue(b);
    const direction = sortDirection === 'asc' ? 1 : -1;
    const result = typeof aValue === 'number' && typeof bValue === 'number'
      ? aValue - bValue
      : String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });

    return result === 0 ? a.originalIndex - b.originalIndex : result * direction;
  });

  const handleSort = (key: HolidaySortKey) => {
    if (sortBy === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(key);
    setSortDirection('asc');
  };

  const renderSortableHeader = (key: HolidaySortKey, label: string) => (
    <TableSortLabel
      active={sortBy === key}
      direction={sortBy === key ? sortDirection : 'asc'}
      onClick={() => handleSort(key)}
    >
      <strong>{label}</strong>
    </TableSortLabel>
  );

  return (
    <Box sx={{ width: '100%', ml: 0, pl: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Manage Holidays</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ textTransform: 'none' }}>
          Add Holiday
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, ml: 0 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            placeholder="Search holiday..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select value={yearFilter} label="Year" onChange={(e) => setYearFilter(Number(e.target.value))}>
              {years.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Total Records: {holidays.length}
        </Typography>
        <TableContainer component={Paper} sx={{ ml: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>{renderSortableHeader('srNo', 'Sr.No.')}</TableCell>
                <TableCell>{renderSortableHeader('name', 'Holiday Name')}</TableCell>
                <TableCell>{renderSortableHeader('date', 'Date')}</TableCell>
                <TableCell>{renderSortableHeader('day', 'Day')}</TableCell>
                <TableCell>{renderSortableHeader('description', 'Description')}</TableCell>
                <TableCell>{renderSortableHeader('organization', 'Organization')}</TableCell>
                <TableCell>{renderSortableHeader('branch', 'Branch')}</TableCell>
                <TableCell>{renderSortableHeader('department', 'Department')}</TableCell>
                <TableCell>{renderSortableHeader('status', 'Status')}</TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedHolidayRows.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center">No holidays found</TableCell></TableRow>
              ) : (
                sortedHolidayRows.map(({ holiday: h, originalIndex, scopes }) => {

                  return (
                    <TableRow key={h.id} hover>
                    <TableCell>{originalIndex + 1}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight={600}>{h.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{new Date(h.date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(h.date).toLocaleDateString('en-US', { weekday: 'long' })}</TableCell>
                    <TableCell>{h.description || '-'}</TableCell>
                    <TableCell>{scopes.organization}</TableCell>
                    <TableCell>{scopes.branch}</TableCell>
                    <TableCell>{scopes.department}</TableCell>
                    <TableCell>
                      <Chip label={h.isActive ? 'Active' : 'Inactive'} size="small"
                        color={h.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenEdit(h)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" disabled={isPastDate(h.date)} onClick={() => handleDelete(h.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              size="small"
              fullWidth
              label="Holiday Name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: 50 }}
              helperText={`${name.length}/50 characters`}
            />
               <TextField size="small" fullWidth label="Date *" type="date" value={date}
                          onChange={(e) => setDate(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          error={Boolean(date) && hasDuplicateHolidayDate(date)}
                          helperText={Boolean(date) && hasDuplicateHolidayDate(date) ? 'A holiday already exists on this date' : ''}
                        />
                        <TextField         
              size="small"
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ maxLength: 200 }}
              helperText={`${description.length}/200 characters`}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Organizations</InputLabel>
              <Select
                multiple
                value={selectedOrganizationIds}
                label="Organizations"
                onChange={(e) => {
                  const values = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  setSelectedOrganizationIds(values);
                  setSelectedBranchIds((current) => current.filter((id) => branches.find((branch) => String(branch.id) === id && branch.organizationId && values.includes(String(branch.organizationId)))));
                  setSelectedDepartmentIds([]);
                }}
                renderValue={(selected) => selected.map((id) => organizations.find((item) => String(item.id) === id)?.name || id).join(', ')}
              >
                {organizations.map((organization) => (
                  <MenuItem key={organization.id} value={String(organization.id)}>
                    <Checkbox checked={selectedOrganizationIds.includes(String(organization.id))} />
                    <ListItemText primary={organization.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Branches</InputLabel>
              <Select
                multiple
                value={selectedBranchIds}
                label="Branches"
                onChange={(e) => {
                  const values = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  setSelectedBranchIds(values);
                  setSelectedDepartmentIds((current) => current.filter((id) => departmentItems.find((department) => String(department.id) === id && department.branchId && values.includes(String(department.branchId)))));
                }}
                renderValue={(selected) => selected.map((id) => branches.find((item) => String(item.id) === id)?.name || id).join(', ')}
              >
                {availableBranches.map((branch) => (
                  <MenuItem key={branch.id} value={String(branch.id)}>
                    <Checkbox checked={selectedBranchIds.includes(String(branch.id))} />
                    <ListItemText primary={branch.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Departments</InputLabel>
              <Select
                multiple
                value={selectedDepartmentIds}
                label="Departments"
                onChange={(e) => {
                  const values = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                  setSelectedDepartmentIds(values);
                }}
                renderValue={(selected) => selected.map((id) => departmentItems.find((item) => String(item.id) === id)?.name || id).join(', ')}
              >
                {availableDepartments.map((department) => (
                  <MenuItem key={department.id} value={String(department.id)}>
                    <Checkbox checked={selectedDepartmentIds.includes(String(department.id))} />
                    <ListItemText primary={department.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading || !name.trim() || !date || buildHolidayTargets().length === 0} sx={{ textTransform: 'none' }}>
            {loading ? <CircularProgress size={20} /> : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Holidays;
